/**
 * Set of cloud functions to help with using cloud services such as file upload that exist outside of our api
**/
import { MsUtils } from '@/utils/msutils'
// This import loads the firebase namespace along with all its type information.
import firebase from 'firebase/app'
// This import loads individual storage service only
import 'firebase/storage'

/**
 * States of a Upload
 */
export const UPLOAD_STATES = {
  PAUSED: 'upload paused',
  RUNNING: 'upload running'
}

/**
 * Upload errors
 */
export const UPLOAD_ERRORS = {
  UNAUTHORIZED: 'unauthorized',
  CANCELLED: 'cancelled',
  UNKNOWN: 'unknown'
}

/**
 * Class representing a snapshot of a file upload
 */
export class UploadSnapshot {
  /**
   * Create an UploadSnapshot.
   * @param {string} name - The file name
   * @param {number} progress - The progress as a value from 0 to 100
   */
  constructor (name, progress) {
    this.name = name
    this.progress = progress
    this.state = null
  }

  /**
   * Get the name value.
   * @returns {string} The name value.
   */
  getName () {
    return this.name
  }

  /**
   * Get the progress value.
   * @returns {number} The progress value between 0 and 100.
   */
  getProgress () {
    return this.progress
  }

  /**
   * Get the state.
   * @returns {UPLOAD_STATES} The state value.
   */
  getState () {
    return this.state
  }
}

export default {

  // private key to store our firebase config options in
  _firebaseConfig: null,
  /**
   * Callback for upload progress events
   *
   * @callback progressCallback
   * @param {UploadSnapshot} UploadSnapshot instance representing the progress.
   */
  /**
   * Callback for upload errors
   *
   * @callback errorCallback
   * @param {UPLOAD_ERRORS} The error that occurred during upload
   */
  /**
   * Callback for upload completion
   *
   * @callback successCallback
   * @param {string} url of completed upload
   */
  /**
   * Upload an image to the cloud
   * @param {string} folder - folder to upload file into
   * @param {Object} file - object to upload
   * @param {progressCallback} progressCallback - function to call on progress updates.
   *
   * @param {errorCallback} errorCallback - function called on file upload error
   * @param {successCallback} successCallback - function call on file upload success
   */
  _uploadToCloud (path, file, fileName, progressCallback, errorCallback, successCallback) {
    // get ref to firebase storage bucket
    let storageRef = firebase.storage().ref()
    let uploadTask = storageRef.child(path).put(file)
    let uploadSnapshot = new UploadSnapshot(fileName, 0)
    // Listen for state changes, errors, and completion of the upload.
    uploadTask.on(firebase.storage.TaskEvent.STATE_CHANGED,
      // change callback
      function (snapshot) {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        switch (snapshot.state) {
          case firebase.storage.TaskState.PAUSED:
            uploadSnapshot.state = UPLOAD_STATES.PAUSED
            break
          case firebase.storage.TaskState.RUNNING:
            uploadSnapshot.state = UPLOAD_STATES.RUNNING
            break
        }
        if (progressCallback) {
          uploadSnapshot.state = UPLOAD_STATES.RUNNING
          uploadSnapshot.progress = progress
          progressCallback(uploadSnapshot)
        }
      },
      // error callback
      function (error) {
        // A full list of error codes is available at
        // https://firebase.google.com/docs/storage/web/handle-errors
        let errorState = UPLOAD_ERRORS.UNKNOWN
        switch (error.code) {
          case 'storage/unauthorized':
            errorState = UPLOAD_ERRORS.UNAUTHORIZED
            break

          case 'storage/canceled':
            errorState = UPLOAD_ERRORS.CANCELLED
            break
        }
        if (errorCallback) {
          errorCallback(errorState)
        }
      },
      // completion callback
      function () {
        // Upload completed successfully, now we can get the download URL
        uploadTask.snapshot.ref.getDownloadURL().then((downloadUrl) => {
          console.log('File available at', downloadUrl)
          let thumbUrl = downloadUrl.replace(fileName, 'thumb_' + fileName)
          if (successCallback) {
            successCallback(downloadUrl, thumbUrl)
          }
        })
      })
  },
  /**
   * Callback for upload progress events
   *
   * @callback progressCallback
   * @param {UploadSnapshot} UploadSnapshot instance representing the progress.
   */
  /**
   * Callback for upload errors
   *
   * @callback errorCallback
   * @param {UPLOAD_ERRORS} The error that occurred during upload
   */
  /**
   * Callback for upload completion
   *
   * @callback successCallback
   * @param {string} url of completed upload
   */
  /**
   * Upload an image to the cloud
   * @param {string} folder - folder to upload file into
   * @param {Object} file - object to upload
   * @param {progressCallback} progressCallback - function to call on progress updates.
   *
   * @param {errorCallback} errorCallback - function called on file upload error
   * @param {successCallback} successCallback - function call on file upload success
   */
  uploadImage (folder, file, progressCallback, errorCallback, successCallback) {
    // Internal to this function we use firebase to manage the upload.
    // As file is uploaded we handle callbacks and wrap them to our generic
    // classes before calling back to app.
    //
    let fileName = MsUtils.uuidv4()
    let path = this._firebaseConfig.storageBucketImagePath + '/' + folder + '/' + fileName
    this._uploadToCloud(path, file, fileName, progressCallback, errorCallback, successCallback)
  },
  /**
   * Callback for upload progress events
   *
   * @callback progressCallback
   * @param {UploadSnapshot} UploadSnapshot instance representing the progress.
   */
  /**
   * Callback for upload errors
   *
   * @callback errorCallback
   * @param {UPLOAD_ERRORS} The error that occurred during upload
   */
  /**
   * Callback for upload completion
   *
   * @callback successCallback
   * @param {string} url of completed upload
   */
  /**
   * Upload an image to the cloud
   * @param {string} folder - folder to upload file into
   * @param {Object} file - object to upload
   * @param {progressCallback} progressCallback - function to call on progress updates.
   *
   * @param {errorCallback} errorCallback - function called on file upload error
   * @param {successCallback} successCallback - function call on file upload success
   */
  uploadFile (folder, file, progressCallback, errorCallback, successCallback) {
    // Internal to this function we use firebase to manage the upload.
    // As file is uploaded we handle callbacks and wrap them to our generic
    // classes before calling back to app.
    //
    let fileName = MsUtils.uuidv4()
    let path = this._firebaseConfig.storageBucketDocumentsPath + '/' + folder + '/' + fileName
    this._uploadToCloud(path, file, fileName, progressCallback, errorCallback, successCallback)
  },
  /**
   * Init the cloud module, required before first use
   * @param {Object} options - initialise options
   * @param {Object} options.firebaseConfig - if using firebase as supporting cloud service this key will be used to
   * initialise the firebase sdk, else leave undefined. For more see [docs]{@https://firebase.google.com/docs/reference/js/firebase#.initializeApp}
   */
  initialize (options) {
    // check for firebase config
    if (options && options.firebaseConfig) {
      // only initialize if we haven't done so already
      if (!firebase.apps.length) {
        console.log('initialising firebase for first use')
        // todo would be nice to check keys to see if we validate the fb config
        this._firebaseConfig = Object.assign({}, options.firebaseConfig)
        firebase.initializeApp(this._firebaseConfig)
      }
    }
  }
}
